import { apiUrl, getToken } from '../../lib/api';

// The measurements behind the speed test (SpeedTest.tsx): this browser ⇄ the server. Ping
// (median + jitter), parallel download streams, then parallel uploads.

const now = () => performance.now();
const TOO_MANY = 'Too many tests in a row — wait a few minutes.';

// A plain GET with no Authorization header: a "simple" cross-origin request, so the browser sends
// no CORS preflight first. With the header, a missed preflight cache doubled the measured ping.
async function pingOnce(): Promise<number> {
  const t0 = now();
  await fetch(apiUrl('/api/health'), { cache: 'no-store' });
  return now() - t0;
}

/**
 * 9 pings one after another (jitter compares each with the one before, so they can't overlap);
 * the first is dropped as it includes connection setup.
 */
export async function measurePing(onProgress: (p: number) => void): Promise<{ ping: number; jitter: number }> {
  const samples = await Array.from({ length: 9 }).reduce<Promise<number[]>>(
    (prev, _, i) =>
      prev.then(async (acc) => {
        const ms = await pingOnce();
        onProgress((i + 1) / 9);
        return [...acc, ms];
      }),
    Promise.resolve([]),
  );
  samples.shift();
  const sorted = [...samples].sort((a, b) => a - b);
  const ping = sorted[Math.floor(sorted.length / 2)];
  const jitter = samples.slice(1).reduce((a, s, i) => a + Math.abs(s - samples[i]), 0) / Math.max(1, samples.length - 1);
  return { ping, jitter };
}

/** Download over 4 parallel streams for ~4 s; reports live Mbit/s, ignoring TCP slow start. */
export async function measureDown(onLive: (mbps: number, progress: number) => void): Promise<number> {
  const ctrl = new AbortController();
  const start = now();
  let bytes = 0;
  let measuredFrom = 0;
  let measuredBytes = 0;
  const rate = (el: number) => (measuredFrom ? ((bytes - measuredBytes) * 8) / ((el - measuredFrom) / 1000) / 1e6 : (bytes * 8) / (el / 1000) / 1e6);
  const stream = async () => {
    const r = await fetch(apiUrl('/api/speed/down?bytes=25165824'), { headers: { Authorization: `Bearer ${getToken()}` }, cache: 'no-store', signal: ctrl.signal });
    if (!r.ok || !r.body) throw new Error(r.status === 429 ? TOO_MANY : `download failed (${r.status})`);
    const reader = r.body.getReader();
    const pump = async (): Promise<void> => {
      const { value, done } = await reader.read();
      if (done) return;
      bytes += value.byteLength;
      const el = now() - start;
      if (el > 1000 && !measuredFrom) {
        measuredFrom = el;
        measuredBytes = bytes; // ignore TCP slow-start in the first second
      }
      return pump();
    };
    return pump();
  };
  const ticker = setInterval(() => {
    const el = now() - start;
    onLive(rate(el), Math.min(1, el / 4000));
    if (el > 4000) ctrl.abort();
  }, 250);
  try {
    const rs = await Promise.allSettled([stream(), stream(), stream(), stream()]);
    const failed = rs.find((r) => r.status === 'rejected' && (r.reason as Error).name !== 'AbortError');
    if (failed && bytes === 0) throw (failed as PromiseRejectedResult).reason;
  } finally {
    clearInterval(ticker); // a failed download must not leave the ticker running
  }
  return rate(now() - start);
}

/** Upload: 2 parallel 8 MB posts of random bytes. */
export async function measureUp(onProgress: (p: number) => void): Promise<number> {
  const block = new Uint8Array(1 << 20);
  for (let i = 0; i < block.length; i += 65536) crypto.getRandomValues(block.subarray(i, i + 65536));
  const blob = new Blob(Array.from({ length: 8 }, () => block));
  const start = now();
  const ticker = setInterval(() => onProgress(Math.min(1, (now() - start) / 4000)), 200);
  try {
    await Promise.all(
      [0, 1].map(() =>
        fetch(apiUrl('/api/speed/up'), { method: 'POST', headers: { Authorization: `Bearer ${getToken()}` }, body: blob }).then((r) => {
          if (!r.ok) throw new Error(r.status === 429 ? TOO_MANY : `upload failed (${r.status})`);
        }),
      ),
    );
  } finally {
    clearInterval(ticker);
  }
  return (blob.size * 2 * 8) / ((now() - start) / 1000) / 1e6;
}
